import { Link } from 'react-router-dom'
import {
  Package,
  Search,
  Camera,
  Settings,
  CheckCircle,
  RotateCcw,
  Database,
  Server,
  Palette,
  ChevronRight,
  DollarSign
} from 'lucide-react'

const features = [
  {
    name: 'Intake',
    href: '/intake',
    icon: Package,
    description: 'Add new inventory items',
    status: 'active',
    glowColor: '#FF6B35'
  },
  {
    name: 'Search',
    href: '/search',
    icon: Search,
    description: 'Find and manage items',
    status: 'active',
    glowColor: '#00F0FF'
  },
  {
    name: 'Scanner',
    href: '/qr-scanner',
    icon: Camera,
    description: 'Scan QR codes and barcodes',
    status: 'active',
    glowColor: '#B91BFF'
  },
  {
    name: 'Returns',
    href: '/returns',
    icon: RotateCcw,
    description: 'Process returns and exchanges',
    status: 'active',
    glowColor: '#FFD93D'
  },
  {
    name: 'Admin',
    href: '/admin',
    icon: Settings,
    description: 'System administration',
    status: 'active',
    glowColor: '#FF1493'
  },
  {
    name: 'EscapeRoute',
    href: '/escaperoute',
    icon: DollarSign,
    description: 'Debt management & financial planning',
    status: 'active',
    glowColor: '#10B981'
  },
]

export default function HomePage() {
  return (
    <div style={{
      padding: '24px',
      background: 'linear-gradient(180deg, #1A1F2E 0%, #242938 100%)',
      minHeight: '100vh',
      fontFamily: 'Rajdhani, sans-serif'
    }}>
      {/* Hero Section */}
      <div style={{
        textAlign: 'center',
        marginBottom: '48px',
        borderBottom: '2px solid #FF6B35',
        paddingBottom: '32px'
      }}>
        <h1 style={{
          fontFamily: 'Rajdhani, sans-serif',
          fontSize: '48px',
          fontWeight: 700,
          color: '#FF6B35',
          textTransform: 'uppercase',
          letterSpacing: '2px',
          margin: '0 0 16px 0',
          textShadow: '0 0 20px rgba(255, 107, 53, 0.6)'
        }}>
          BGS CORE // INVENTORY
        </h1>
        <p style={{
          fontFamily: 'Rajdhani, sans-serif',
          fontSize: '16px',
          color: '#8A95AA',
          maxWidth: '800px',
          margin: '0 auto 24px auto',
          letterSpacing: '1px'
        }}>
          Complete inventory management and operations platform with AI integration,
          mobile PWA support, and comprehensive dark theme.
        </p>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '16px',
          flexWrap: 'wrap'
        }}>
          <div style={{
            background: 'rgba(0, 255, 0, 0.1)',
            border: '2px solid #00FF00',
            borderRadius: '6px',
            padding: '8px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            boxShadow: '0 0 10px rgba(0, 255, 0, 0.3)'
          }}>
            <CheckCircle style={{ width: 16, height: 16, color: '#00FF00' }} />
            <span style={{
              color: '#00FF00',
              fontSize: '12px',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '1px'
            }}>
              Production Ready
            </span>
          </div>
          <div style={{
            background: 'rgba(0, 240, 255, 0.1)',
            border: '2px solid #00F0FF',
            borderRadius: '6px',
            padding: '8px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            boxShadow: '0 0 10px rgba(0, 240, 255, 0.3)'
          }}>
            <CheckCircle style={{ width: 16, height: 16, color: '#00F0FF' }} />
            <span style={{
              color: '#00F0FF',
              fontSize: '12px',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '1px'
            }}>
              Dark Theme
            </span>
          </div>
          <div style={{
            background: 'rgba(0, 255, 0, 0.1)',
            border: '2px solid #00FF00',
            borderRadius: '6px',
            padding: '8px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            boxShadow: '0 0 10px rgba(0, 255, 0, 0.3)'
          }}>
            <CheckCircle style={{ width: 16, height: 16, color: '#00FF00' }} />
            <span style={{
              color: '#00FF00',
              fontSize: '12px',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '1px'
            }}>
              Mobile PWA
            </span>
          </div>
        </div>
      </div>

      {/* Features Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '20px',
        marginBottom: '48px'
      }}>
        {features.map((feature) => (
          <Link
            key={feature.name}
            to={feature.status === 'active' ? feature.href : '#'}
            style={{
              textDecoration: 'none',
              display: 'block',
              cursor: feature.status === 'active' ? 'pointer' : 'not-allowed',
              opacity: feature.status === 'active' ? 1 : 0.6
            }}
          >
            <div style={{
              background: 'linear-gradient(135deg, #1A1F2E, #242938)',
              border: `2px solid ${feature.glowColor}`,
              borderRadius: '8px',
              padding: '24px',
              transition: 'all 0.3s ease',
              boxShadow: `0 0 20px ${feature.glowColor}40`,
              position: 'relative',
              overflow: 'hidden',
              height: '100%'
            }}
            onMouseEnter={(e) => {
              if (feature.status === 'active') {
                e.currentTarget.style.transform = 'translateY(-4px)'
                e.currentTarget.style.boxShadow = `0 0 30px ${feature.glowColor}80`
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)'
              e.currentTarget.style.boxShadow = `0 0 20px ${feature.glowColor}40`
            }}
            >
              {/* Corner Accent */}
              <div style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '100px',
                height: '100px',
                background: `linear-gradient(135deg, transparent 50%, ${feature.glowColor}20 50%)`,
                pointerEvents: 'none'
              }} />

              {/* Icon */}
              <div style={{
                background: `${feature.glowColor}20`,
                border: `2px solid ${feature.glowColor}`,
                borderRadius: '8px',
                padding: '12px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '16px'
              }}>
                <feature.icon style={{ width: 28, height: 28, color: feature.glowColor }} />
              </div>

              {/* Title */}
              <h3 style={{
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '24px',
                fontWeight: 700,
                color: feature.glowColor,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                margin: '0 0 8px 0',
                textShadow: `0 0 10px ${feature.glowColor}60`
              }}>
                {feature.name}
              </h3>

              {/* Description */}
              <p style={{
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '14px',
                color: '#8A95AA',
                margin: '0 0 20px 0',
                letterSpacing: '0.5px',
                minHeight: '42px'
              }}>
                {feature.description}
              </p>

              {/* Status Badge */}
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                background: feature.status === 'active' ? 'rgba(0, 255, 0, 0.1)' : 'rgba(138, 149, 170, 0.1)',
                border: `1px solid ${feature.status === 'active' ? '#00FF00' : '#8A95AA'}`,
                borderRadius: '6px',
                padding: '6px 12px',
                marginBottom: '16px'
              }}>
                <span style={{
                  fontSize: '11px',
                  color: feature.status === 'active' ? '#00FF00' : '#8A95AA',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '1px'
                }}>
                  {feature.status === 'active' ? 'ACTIVE' : 'SOON'}
                </span>
              </div>

              {/* Action Link */}
              {feature.status === 'active' && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: '8px',
                  color: feature.glowColor,
                  fontSize: '12px',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  paddingTop: '16px',
                  borderTop: '1px solid rgba(138, 149, 170, 0.2)'
                }}>
                  ACCESS
                  <ChevronRight style={{ width: 16, height: 16 }} />
                </div>
              )}
            </div>
          </Link>
        ))}
      </div>

      {/* System Status Section */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(255, 107, 53, 0.1), rgba(255, 107, 53, 0.05))',
        border: '2px solid #FF6B35',
        borderRadius: '8px',
        padding: '32px',
        boxShadow: '0 0 20px rgba(255, 107, 53, 0.2)'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '12px'
        }}>
          <Server style={{ width: 24, height: 24, color: '#FF6B35' }} />
          <h3 style={{
            fontFamily: 'Rajdhani, sans-serif',
            fontSize: '24px',
            fontWeight: 700,
            color: '#FF6B35',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            margin: 0,
            textShadow: '0 0 10px rgba(255, 107, 53, 0.6)'
          }}>
            System Status
          </h3>
        </div>
        <p style={{
          fontFamily: 'Rajdhani, sans-serif',
          fontSize: '14px',
          color: '#8A95AA',
          margin: '0 0 32px 0',
          letterSpacing: '0.5px'
        }}>
          Current system health and performance metrics
        </p>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '24px'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              background: 'rgba(0, 255, 0, 0.1)',
              border: '2px solid #00FF00',
              borderRadius: '50%',
              width: '80px',
              height: '80px',
              margin: '0 auto 16px auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 0 20px rgba(0, 255, 0, 0.3)'
            }}>
              <Database style={{ width: 40, height: 40, color: '#00FF00' }} />
            </div>
            <h3 style={{
              fontFamily: 'Rajdhani, sans-serif',
              fontSize: '20px',
              fontWeight: 700,
              color: '#E0E6F0',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              margin: '0 0 8px 0'
            }}>
              Database
            </h3>
            <p style={{
              fontFamily: 'Rajdhani, sans-serif',
              fontSize: '14px',
              color: '#8A95AA',
              margin: 0,
              letterSpacing: '0.5px'
            }}>
              PostgreSQL Connected
            </p>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              background: 'rgba(0, 255, 0, 0.1)',
              border: '2px solid #00FF00',
              borderRadius: '50%',
              width: '80px',
              height: '80px',
              margin: '0 auto 16px auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 0 20px rgba(0, 255, 0, 0.3)'
            }}>
              <Server style={{ width: 40, height: 40, color: '#00FF00' }} />
            </div>
            <h3 style={{
              fontFamily: 'Rajdhani, sans-serif',
              fontSize: '20px',
              fontWeight: 700,
              color: '#E0E6F0',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              margin: '0 0 8px 0'
            }}>
              API
            </h3>
            <p style={{
              fontFamily: 'Rajdhani, sans-serif',
              fontSize: '14px',
              color: '#8A95AA',
              margin: 0,
              letterSpacing: '0.5px'
            }}>
              All Endpoints Active
            </p>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              background: 'rgba(0, 255, 0, 0.1)',
              border: '2px solid #00FF00',
              borderRadius: '50%',
              width: '80px',
              height: '80px',
              margin: '0 auto 16px auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 0 20px rgba(0, 255, 0, 0.3)'
            }}>
              <Palette style={{ width: 40, height: 40, color: '#00FF00' }} />
            </div>
            <h3 style={{
              fontFamily: 'Rajdhani, sans-serif',
              fontSize: '20px',
              fontWeight: 700,
              color: '#E0E6F0',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              margin: '0 0 8px 0'
            }}>
              Theme
            </h3>
            <p style={{
              fontFamily: 'Rajdhani, sans-serif',
              fontSize: '14px',
              color: '#8A95AA',
              margin: 0,
              letterSpacing: '0.5px'
            }}>
              Dark Mode Ready
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
